import parse, { DOMNode, Element } from 'html-react-parser'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import LinearProgressWithLabel from '@components/LinearProgressWithLabel'
import TruncateLines from '@components/TruncateLines'
import { ProfileAlert } from '@modules/alerts/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import { TextWithLinks } from '@modules/profile/TextWithLink'
import {
  ProfileSection,
  ProfileSectionTitle,
  StyledSocialIcon,
  SubsectionContent,
  SubsectionContentInner,
  SubsectionTitle,
  UpdatedLabel,
} from '@modules/profile/index.styles'
import { AnyProfile, Company, ProfileType } from '@modules/search/types'
import Link from '@mui/material/Link'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { calcStageProgress } from '@shared/utils'

import { PROFILE_FOLLOWED_SECTIONS, PROFILE_FOLLOWED_SUB_SECTIONS, PROFILE_VIEW_CONFIG } from './profileConfig'
import { getFieldIsUpdated } from './utils'

export type ProfileAreaType = 'mainInfo' | 'secondColumnInfo' | 'thirdColumnInfo' | 'companyCard'

interface ProfileDetailsProps {
  profileData: AnyProfile
  profileType: ProfileType
  profileAreaName: ProfileAreaType
  profileId?: number
  profileAlert?: ProfileAlert
}

export interface ContentViewProps {
  id?: string
  subTitle?: string
  fieldName: string
  joinSymbol?: string
  isLink?: boolean
  linkDomain?: string
  iconSrc?: string
  isEmail?: boolean
  isBold?: boolean
  isList?: boolean
  isNewLine?: boolean
  noBottomMargin?: boolean
  isProgressBar?: boolean
  progressStage?: string[]
  truncateToLines?: number
  isUpdated?: boolean
  hasNewActivity?: boolean
  renderComponent?: () => void
}

type ContentViewDataType = string | string[] | number | LinkContentProps

interface LinkContentProps extends ContentViewProps {
  customFileName?: string
  url?: string
  data?: string
}

type LinkViewProps = string | LinkContentProps

export interface SectionProps {
  title: string
  subSections: ContentViewProps[]
  isUpdated?: boolean
  subscriberAllowed?: boolean
  ecommerceAllowed?: boolean
}

export interface JoinedFieldsProps {
  joinedFieldsKey: string
  subTitle?: string
  isUpdated?: boolean
  hasNewActivity?: boolean
  joinedFieldsConfig: ContentViewProps[]
}

export type SubSectionType = ContentViewProps | JoinedFieldsProps

const replaceHtmlFunction = (node: DOMNode) => {
  const domNode = node as Element

  try {
    if (domNode.name === 'a') {
      const getChildrenData = (elem: Element): string =>
        (elem.children[0] as { data: string })?.data || getChildrenData(elem.children[0] as Element)
      return (
        <Link href={domNode.attribs.href} target="_blank" rel="noopener noreferrer" underline="none">
          {getChildrenData(domNode)}
        </Link>
      )
    }

    if (node?.type === 'text' && typeof node.data === 'string') {
      return <TextWithLinks text={node.data} />
    }
    return domNode
  } catch (error) {
    return domNode
  }
}

const getProfileSectionData = (fieldName: string, profileData: Company) => {
  if (!fieldName) return null
  const propPath = fieldName && fieldName.split('.')
  if (propPath && propPath.length > 1) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    return propPath.reduce((propObj, propKey: string) => propObj && propObj[propKey], profileData)
  }
  return profileData[fieldName as keyof Company]
}

const renderLink = ({ config, fieldContent }: { config: ContentViewProps; fieldContent: LinkViewProps }) => {
  const linkUrl = typeof fieldContent === 'string' ? fieldContent : fieldContent.data || fieldContent.url
  if (!linkUrl) return null
  const linkText = typeof fieldContent === 'string' ? fieldContent : fieldContent.customFileName || fieldContent.data || linkUrl
  const { iconSrc, fieldName, isEmail } = config
  const withHttp = (url: string): string => (!/^https?:\/\//i.test(url) ? `https://${url}` : url)
  const fullUrl = isEmail ? `mailto:${linkUrl}` : withHttp(linkUrl)

  return (
    <Link
      id={config.id}
      sx={{ marginRight: '5px' }}
      href={fullUrl}
      target="_blank"
      rel="noopener"
      underline="none"
      data-testid={`${fieldName}_link`}
    >
      {iconSrc ? <StyledSocialIcon src={iconSrc} alt={fieldName} data-testid={`${fieldName}_link_icon`} /> : linkText}
    </Link>
  )
}

const renderFieldContent = ({ config, content }: { config: ContentViewProps; content: ContentViewDataType }) => {
  const { isLink, isEmail, isBold, isList, isNewLine, isProgressBar, progressStage, truncateToLines } = config

  let fieldContent: ContentViewDataType | ReactNode = content

  if (typeof content === 'string') {
    fieldContent = content.replace(/(prompt|alert)\(.+\)/g, val => `"'${val}'"`)
    if (!isLink && !isEmail) {
      fieldContent = parse(fieldContent as string, { replace: replaceHtmlFunction })
    }
  }

  if (truncateToLines) {
    return <TruncateLines lines={truncateToLines as number}>{fieldContent as string}</TruncateLines>
  }

  if (isList && Array.isArray(content)) {
    fieldContent = (content as string[]).filter(i => i && i.length).join(', ')
  }
  if (isLink || isEmail) {
    fieldContent = renderLink({ config, fieldContent: fieldContent as LinkViewProps }) as typeof fieldContent
  }
  if (isBold) {
    fieldContent = <strong>{fieldContent as ReactNode}</strong>
  }
  if (isNewLine) {
    fieldContent = <div>{fieldContent as ReactNode}</div>
  }

  if (isProgressBar) {
    fieldContent = (
      <LinearProgressWithLabel
        label={content as string}
        value={calcStageProgress(progressStage as string[], content as string)}
      />
    )
  }

  return fieldContent
}

const renderSubSectionsList = ({
  subSections,
  profileData,
  profileId,
  noBottomMargin,
}: {
  subSections: SubSectionType[]
  profileData: AnyProfile
  profileId?: number
  noBottomMargin?: boolean
}) => {
  return subSections.map((subSection: SubSectionType) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    const { fieldName, joinedFieldsKey, joinedFieldsConfig, renderComponent } = subSection

    if (renderComponent) {
      return (
        <SubsectionContent key={`${fieldName}_testid`} data-testid={`${fieldName}_testid`} noBottomMargin={noBottomMargin}>
          {renderComponent({ profileData, profileId, isHighlighted: subSection.hasNewActivity })}
        </SubsectionContent>
      )
    }

    let subSectionData

    if ((subSection as JoinedFieldsProps).joinedFieldsKey) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      const joinedFieldsValues = []
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      joinedFieldsConfig.forEach(fieldConfig => {
        const profileSubsectionData = getProfileSectionData(fieldConfig.fieldName, profileData as Company)
        if (profileSubsectionData) {
          joinedFieldsValues.push({
            data: profileSubsectionData,
            itemConfig: fieldConfig,
          })
        }
      })
      if (joinedFieldsValues.length) {
        subSectionData = {
          joinedFieldsKey,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore:next-line
          values: joinedFieldsValues,
        }
      }
    } else {
      subSectionData = getProfileSectionData(fieldName, profileData as Company)
    }

    const notEmptySubSectionData = Array.isArray(subSectionData) ? !!subSectionData.length : subSectionData

    return notEmptySubSectionData ? (
      <SubSection
        subSectionConfig={subSection}
        subSectionData={subSectionData as ContentViewDataType}
        key={`${fieldName || joinedFieldsKey}-key`}
      />
    ) : null
  })
}

const SubSection = ({
  subSectionConfig,
  subSectionData,
}: {
  subSectionConfig: SubSectionType
  subSectionData: ContentViewDataType
}) => {
  const { subTitle, isUpdated } = subSectionConfig
  const { joinedFieldsKey } = subSectionConfig as JoinedFieldsProps
  const { noBottomMargin, fieldName } = subSectionConfig as ContentViewProps
  const { t } = useTranslation()
  let sectionContent

  if (joinedFieldsKey) {
    sectionContent = (
      // eslint-disable-next-line react/jsx-no-comment-textnodes
      <React.Fragment key={`${joinedFieldsKey}-key`}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore:next-line */}
        {subSectionData.values.map((item, index: number) => {
          const { data, itemConfig, itemConfig: { joinSymbol = ' ' } = {} } = item
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore:next-line
          const startOfLine = subSectionData.values[index - 1]?.itemConfig?.isNewLine
          return (
            <React.Fragment key={`${itemConfig.fieldName}-key`}>
              {!startOfLine && index > 0 && joinSymbol}
              {renderFieldContent({ config: itemConfig, content: data })}
            </React.Fragment>
          )
        })}
      </React.Fragment>
    )
  } else {
    sectionContent = renderFieldContent({ config: subSectionConfig as ContentViewProps, content: subSectionData })
  }

  return (
    <SubsectionContent
      noBottomMargin={noBottomMargin}
      isUpdated={isUpdated}
      data-testid={`${fieldName || joinedFieldsKey}_testid`}
    >
      {subTitle && (
        <SubsectionTitle variant="h3" data-testid={`${fieldName || joinedFieldsKey}_subtitle_testid`}>
          {t(translationKeys[subTitle as keyof typeof translationKeys])}
        </SubsectionTitle>
      )}
      <SubsectionContentInner>{sectionContent as ReactNode}</SubsectionContentInner>
      {isUpdated && <UpdatedLabel>{t(translationKeys.UPDATED)}</UpdatedLabel>}
    </SubsectionContent>
  )
}

export const ProfileElements = ({
  profileSectionConfig,
  profileData,
  profileId,
  profileAreaName,
  hasAccessToAlerts,
  noMargin,
}: {
  profileSectionConfig: SectionProps[]
  profileData: AnyProfile
  profileId?: number
  profileAreaName: ProfileAreaType
  hasAccessToAlerts: boolean
  noMargin?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <>
      {profileSectionConfig.map((section, index: number) => {
        const { title, subSections, subscriberAllowed, ecommerceAllowed } = section
        if ((subscriberAllowed && !hasAccessToAlerts) || (ecommerceAllowed && !hasAccessToAlerts)) {
          return null
        }

        const subSectionsList = renderSubSectionsList({ subSections, profileData, profileId, noBottomMargin: noMargin }).filter(
          subSection => subSection,
        )

        return subSectionsList.length ? (
          <ProfileSection
            key={`${title ? title.toLowerCase() : profileAreaName + index}-key`}
            data-testid={`${title ? title.toLowerCase() : profileAreaName + index}_testid`}
            isUpdated={section.isUpdated}
            noMargin={noMargin}
          >
            {title && (
              <ProfileSectionTitle variant="h3">{t(translationKeys[title as keyof typeof translationKeys])}</ProfileSectionTitle>
            )}
            {subSectionsList}
            {section.isUpdated && <UpdatedLabel isAbsolute>{t(translationKeys.UPDATED)}</UpdatedLabel>}
          </ProfileSection>
        ) : null
      })}
    </>
  )
}

const ProfileDetails = (props: ProfileDetailsProps) => {
  const { profileType, profileData, profileAreaName, profileId, profileAlert } = props
  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)

  const { hasAccessToAlerts } = useFeatureFlags()

  const profileSectionConfig = PROFILE_VIEW_CONFIG[profileType as ProfileType][profileAreaName] as SectionProps[]
  const profileFollowedSubSections = PROFILE_FOLLOWED_SUB_SECTIONS[profileType as ProfileType]
  const profileFollowedSections = PROFILE_FOLLOWED_SECTIONS[profileType as ProfileType]

  const getMappedSectionsConfig = () => {
    return profileSectionConfig.map(section => {
      const followedSection = section.subSections?.find(subSection => profileFollowedSections?.includes(subSection.fieldName))
      if (followedSection) {
        const isUpdated =
          hasAccessToAlerts &&
          getFieldIsUpdated({
            fieldName: followedSection?.fieldName,
            profileData,
            lastViewedAt: profileAlert?.lastViewedAt,
          })

        return { ...section, isUpdated }
      }

      const hasNewActivity = hasAccessToAlerts && profileAlert?.hasNewActivity

      const subSections = section.subSections?.map(subSection => {
        if (!profileFollowedSubSections?.includes(subSection.fieldName)) {
          return { ...subSection, hasNewActivity }
        }

        const isUpdated =
          hasAccessToAlerts &&
          getFieldIsUpdated({
            fieldName: subSection.fieldName,
            profileData,
            lastViewedAt: profileAlert?.lastViewedAt,
          })

        return { ...subSection, isUpdated, hasNewActivity }
      })

      return { ...section, subSections }
    })
  }

  const mappedSectionsConfig = getMappedSectionsConfig()

  return (
    <ProfileElements
      profileSectionConfig={mappedSectionsConfig}
      profileData={profileData}
      profileId={profileId}
      profileAreaName={profileAreaName}
      hasAccessToAlerts={hasAccessToAlerts || isEcommerce}
    />
  )
}

export default ProfileDetails
